import React, { useEffect, useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Save, Upload, Loader2, Plus, Trash2 } from "lucide-react";

const GROUPS = [
  { title: "Homepage — Hero", fields: [
    { k: "home_hero_eyebrow", l: "Aanhef", t: "text", d: "Internationale kinderstemcasting" },
    { k: "home_hero_line1", l: "Titel regel 1", t: "text", d: "Echte kinderen." },
    { k: "home_hero_line2", l: "Titel regel 2", t: "text", d: "Echte stemmen." },
    { k: "home_hero_line3", l: "Titel regel 3 (accent)", t: "text", d: "Wereldwijd." },
    { k: "home_hero_subtitle", l: "Ondertitel", t: "textarea", d: "Professionele kinderstem acteurs in 30+ talen. Vind, beluister, shortlist en cast de perfecte kinderstemmen voor je productie." },
    { k: "home_hero_cta1", l: "Primaire knop", t: "text", d: "Vind een stem" },
    { k: "home_hero_cta2", l: "Secundaire knop", t: "text", d: "Wij casten voor jou" },
    { k: "home_hero_link", l: "AI-link", t: "text", d: "Probeer direct AI-stemmen" },
  ] },
  { title: "Homepage — Kopteksten", fields: [
    { k: "home_featured_eyebrow", l: "Uitgelicht — aanhef", t: "text", d: "Uitgelichte stemmen" },
    { k: "home_featured_title", l: "Uitgelicht — titel", t: "text", d: "Deze wil je in je productie!" },
    { k: "home_brands_title", l: "Merkenbalk — titel", t: "text", d: "Vertrouwd door merken wereldwijd" },
  ] },
  { title: "Over ons-pagina", fields: [
    { k: "about_title", l: "Kop", t: "text", d: "De specialist in kinderstemmen" },
    { k: "about_p1", l: "Alinea 1", t: "textarea", d: "" },
    { k: "about_p2", l: "Alinea 2", t: "textarea", d: "" },
    { k: "about_image", l: "Foto", t: "image", d: "" },
  ] },
  { title: "Studio-pagina", fields: [
    { k: "studio_intro", l: "Introtekst", t: "textarea", d: "Een eigen opnamestudio in Nederland en partnerstudio's over de hele wereld. Elk kind wordt begeleid door een gespecialiseerde regisseur en ouders of verzorgers zijn altijd bij de opname aanwezig." },
    { k: "studio_equipment", l: "Apparatuurlijst (één item per regel)", t: "textarea", d: "Neumann U87 microfoons\nSennheiser MKH416 microfoon\nTrident S80 pre-amps\nADL1000 buiscompressor\nSSL G-channel hardware\nDBX160 hardware\nFocusrite interface\nQuested / KRK / Avantone monitoren\nPro Tools Ultimate DAW\nStudioBricks vocal booth" },
    { k: "studio_image", l: "Studiofoto (tussen secties)", t: "image", d: "" },
  ] },
  { title: "Contact-pagina", fields: [
    { k: "contact_form_url", l: "Contactformulier-URL (extern, bijv. forms.app)", t: "text", d: "https://eu.forms.app/blue1media/contact-form-1-1" },
    { k: "contact_intake_url", l: "Intake-/aanmeldformulier-URL (extern)", t: "text", d: "https://eu.forms.app/blue1media/aanmelding-voiceoverkids" },
    { k: "contact_email", l: "E-mail", t: "text", d: "bookings@voiceoverkids.agency" },
    { k: "contact_phone", l: "Telefoon", t: "text", d: "+31 71 2032441" },
    { k: "contact_address", l: "Adres", t: "textarea", d: "VoiceOverKids.Agency\nDe Savornin Lohmanplein 2\n2314 EV Leiden, Nederland" },
  ] },
  { title: "Podcast-pagina", fields: [
    { k: "podcast_title", l: "Kop", t: "text", d: "De VoiceOverKids Podcast" },
    { k: "podcast_intro", l: "Introtekst", t: "textarea", d: "" },
    { k: "podcast_sidebar_title", l: "Titel zijpaneel (naast de video's)", t: "text", d: "Over de show" },
    { k: "podcast_sidebar", l: "Tekst zijpaneel (naast de video's)", t: "textarea", d: "" },
  ] },
  { title: "Social media-links (header-iconen)", fields: [
    { k: "social_tiktok", l: "TikTok-URL", t: "text", d: "https://www.tiktok.com/@..." },
    { k: "social_instagram", l: "Instagram-URL", t: "text", d: "https://www.instagram.com/..." },
    { k: "social_facebook", l: "Facebook-URL", t: "text", d: "https://www.facebook.com/..." },
    { k: "social_youtube", l: "YouTube-URL", t: "text", d: "https://www.youtube.com/@voiceoverkidsagency" },
    { k: "social_spotify", l: "Spotify-URL", t: "text", d: "https://open.spotify.com/..." },
  ] },
];

export default function AdminContent() {
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const refs = useRef({});

  useEffect(() => {
    api.get("/admin/content").then((r) => {
      const data = r.data || {};
      setVals(data);
      // Podcast episodes editor: prefer structured JSON, else migrate legacy
      // one-link-per-line `podcast_videos`.
      let eps = [];
      try { if (data.podcast_episodes) eps = JSON.parse(data.podcast_episodes); } catch (e) { eps = []; }
      if (!Array.isArray(eps) || eps.length === 0) {
        eps = (data.podcast_videos || "").split(/[\n,]+/).map((s) => s.trim()).filter(Boolean)
          .map((u) => ({ url: u, description: "" }));
      }
      setEpisodes(eps);
    }).catch(() => {});
  }, []);
  const set = (k, v) => setVals((p) => ({ ...p, [k]: v }));

  const syncEpisodes = (eps) => {
    setEpisodes(eps);
    setVals((p) => ({ ...p, podcast_episodes: JSON.stringify(eps) }));
  };
  const addEpisode = () => syncEpisodes([...episodes, { url: "", description: "" }]);
  const removeEpisode = (i) => syncEpisodes(episodes.filter((_, idx) => idx !== i));
  const updateEpisode = (i, key, value) => syncEpisodes(episodes.map((e, idx) => idx === i ? { ...e, [key]: value } : e));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/content", { updates: { ...vals, podcast_episodes: JSON.stringify(episodes) } }); toast.success("Opgeslagen — wijzigingen staan live op de site"); }
    catch (e) { toast.error("Opslaan mislukt"); } finally { setSaving(false); }
  };

  const upload = async (k, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(k);
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "content");
    try { const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set(k, data.url); toast.success("Geüpload"); }
    catch (err) { toast.error("Uploaden mislukt"); } finally { setBusy(null); e.target.value = ""; }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold">Pagina-inhoud</h1><p className="mt-1 text-sm text-zinc-500">Bewerk de tekst en afbeeldingen op je website. Laat een veld leeg om de standaardtekst te gebruiken.</p></div>
        <button onClick={save} disabled={saving} data-testid="save-content" className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-violetHover disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Alles opslaan
        </button>
      </div>

      {GROUPS.map((g) => (
        <div key={g.title} className="mt-5 rounded-xl border border-zinc-200 bg-white p-5">
          <h2 className="font-heading font-bold">{g.title}</h2>
          <div className="mt-3 space-y-4">
            {g.fields.map((f) => (
              <div key={f.k}>
                <Label className="label-eyebrow text-zinc-500">{f.l}</Label>
                {f.t === "image" ? (
                  <div className="mt-1 flex items-center gap-3">
                    <div className="h-16 w-24 overflow-hidden rounded-lg bg-zinc-100">{vals[f.k] && <img src={mediaUrl(vals[f.k])} alt="" className="h-full w-full object-cover" />}</div>
                    <input ref={(el) => (refs.current[f.k] = el)} type="file" accept="image/*" hidden onChange={(e) => upload(f.k, e)} data-testid={`content-img-${f.k}`} />
                    <button onClick={() => refs.current[f.k]?.click()} disabled={busy === f.k} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 px-3 py-2 text-xs font-semibold hover:border-brand-violet">
                      {busy === f.k ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Uploaden
                    </button>
                  </div>
                ) : f.t === "textarea" ? (
                  <Textarea value={vals[f.k] ?? ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.d} rows={3} data-testid={`content-${f.k}`} className="mt-1 rounded-lg" />
                ) : (
                  <Input value={vals[f.k] ?? ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.d} data-testid={`content-${f.k}`} className="mt-1 h-10 rounded-lg" />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Podcast episodes — video link + description shown beside each video */}
      <div className="mt-5 rounded-xl border border-zinc-200 bg-white p-5">
        <h2 className="font-heading font-bold">Podcast — afleveringen (video + tekst)</h2>
        <p className="mt-1 text-sm text-zinc-500">Voeg een YouTube-link toe en de tekst die naast die video op de Podcast-pagina wordt getoond.</p>
        <div className="mt-4 space-y-4">
          {episodes.length === 0 && <p className="text-sm text-zinc-400">Nog geen afleveringen. Voeg hieronder je eerste toe.</p>}
          {episodes.map((ep, i) => (
            <div key={i} data-testid={`podcast-episode-${i}`} className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
              <div className="flex items-center justify-between">
                <span className="label-eyebrow text-zinc-500">Aflevering {i + 1}</span>
                <button onClick={() => removeEpisode(i)} data-testid={`remove-episode-${i}`} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-400 hover:text-red-500">
                  <Trash2 size={13} /> Verwijderen
                </button>
              </div>
              <div className="mt-2 space-y-2">
                <Input value={ep.url ?? ""} onChange={(e) => updateEpisode(i, "url", e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=… of https://youtu.be/…"
                  data-testid={`episode-url-${i}`} className="h-10 rounded-lg bg-white" />
                <Textarea value={ep.description ?? ""} onChange={(e) => updateEpisode(i, "description", e.target.value)}
                  placeholder="Beschrijving / shownotes die naast deze video verschijnen…" rows={3}
                  data-testid={`episode-desc-${i}`} className="rounded-lg bg-white" />
              </div>
            </div>
          ))}
          <button onClick={addEpisode} data-testid="add-episode" className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-zinc-300 px-4 py-2 text-sm font-semibold text-zinc-600 hover:border-brand-violet hover:text-brand-violet">
            <Plus size={15} /> Aflevering toevoegen
          </button>
        </div>
      </div>
    </div>
  );
}
